import {
  PREVENTION_CONTROL_TYPES,
  type ConfigControlSpec,
  type IpsControlSpec,
  type NetworkControlSpec,
  type PreventionControlSpec,
} from './prevention.schema';
import type { PreventionCollateralContext } from './prevention-collateral.types';

const WEB_LISTENER_PORTS = new Set([80, 443]);

const isWebListenerPort = ({
  port,
  applicationProtocol,
}: {
  port: number;
  applicationProtocol?: string;
}): boolean => {
  if (WEB_LISTENER_PORTS.has(port)) {
    return true;
  }

  const normalized = applicationProtocol?.trim().toLowerCase() ?? '';

  return (
    normalized === 'http' ||
    normalized === 'https' ||
    normalized === 'http_proxy' ||
    normalized === 'ssl'
  );
};

export const collectPreventionOverBlockingViolations = ({
  spec,
  collateral,
}: {
  spec: PreventionControlSpec;
  collateral?: PreventionCollateralContext | null;
}): string[] => {
  if (spec.controlType === PREVENTION_CONTROL_TYPES.IPS) {
    return collectIpsOverBlockingViolations({
      spec,
      collateral: collateral?.ips,
    });
  }

  if (!collateral) {
    if (
      spec.controlType === PREVENTION_CONTROL_TYPES.NETWORK &&
      isWebListenerPort({ port: spec.targetPort })
    ) {
      return [
        `Network rule denies port ${spec.targetPort}, which is a web listener port. Deny the exposed attacker's source CIDR on the offending allow rule instead of removing the listener port.`,
      ];
    }

    return [];
  }

  switch (spec.controlType) {
    case PREVENTION_CONTROL_TYPES.NETWORK:
      return collectNetworkOverBlockingViolations({
        spec,
        collateral: collateral.network,
      });
    case PREVENTION_CONTROL_TYPES.CONFIG:
      return collectConfigOverBlockingViolations({
        spec,
        collateral: collateral.config,
      });
    default:
      return [];
  }
};

const collectNetworkOverBlockingViolations = ({
  spec,
  collateral,
}: {
  spec: NetworkControlSpec;
  collateral?: PreventionCollateralContext['network'];
}): string[] => {
  const violations: string[] = [];

  if (!collateral) {
    if (isWebListenerPort({ port: spec.targetPort })) {
      violations.push(
        `Network rule denies port ${spec.targetPort}, which is a web listener port. Deny the exposed attacker's source CIDR on the offending allow rule instead of removing the listener port.`,
      );
    }

    return violations;
  }

  if (collateral.isPrimaryListener) {
    violations.push(
      `Network rule denies port ${spec.targetPort}, which is the host's primary web listener. Deny the exposed attacker's source CIDR on the offending allow rule instead of removing the listener port.`,
    );
  }

  if (spec.protocol === 'all') {
    violations.push(
      'Network rule uses protocol "all". Deny only the exposed protocol from the finding.',
    );
  }

  return violations;
};

const collectConfigOverBlockingViolations = ({
  spec,
  collateral,
}: {
  spec: ConfigControlSpec;
  collateral?: PreventionCollateralContext['config'];
}): string[] => {
  if (
    spec.change === 'disablePublicAccess' &&
    collateral?.servesPublicTraffic
  ) {
    return [
      'disablePublicAccess would remove public access to a resource that serves live application traffic. Pick a narrower change or document the availability tradeoff in rationale.',
    ];
  }

  return [];
};

const collectIpsOverBlockingViolations = ({
  spec,
  collateral,
}: {
  spec: IpsControlSpec;
  collateral?: PreventionCollateralContext['ips'];
}): string[] => {
  const violations: string[] = [];

  if (
    (spec.action === 'drop' || spec.action === 'reject') &&
    !spec.targetIp &&
    !spec.cidr &&
    !spec.sensorScope
  ) {
    violations.push(
      `IPS ${spec.action} rule has no targetIp, cidr, or sensorScope. Scope the signature before using drop or reject.`,
    );
  }

  if (
    (spec.action === 'drop' || spec.action === 'reject') &&
    collateral?.internalServicePorts &&
    collateral.internalServicePorts.length > 0 &&
    !spec.targetIp &&
    !spec.cidr
  ) {
    violations.push(
      'IPS drop/reject is host-wide but internal services are present on this host. Prefer alert or add targetIp/cidr scope.',
    );
  }

  return violations;
};

export { isWebListenerPort, WEB_LISTENER_PORTS };
